import { useMemo } from 'react'
import { Alert, Box, Card, CircularProgress, Grid, Typography } from '@mui/material'
import { canonicalPeriodLabels, CanonicalPeriod, MetricState, PERIODS } from '../../common'
import { usePropertyClimateData, type PropertyResult } from '../../../../hooks/climateData'
import { HeatStressScoreBadge } from '../../common/heatStress/utils'

const formatNumber = (value: number | null, digits = 1) => {
  if (value == null || Number.isNaN(value)) return '—'
  return value.toLocaleString(undefined, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  })
}

const getCanonicalValue = (
  metric: PropertyResult | undefined,
  tier: string,
  period: CanonicalPeriod,
): number | null => {
  if (!metric || metric.state?.value !== MetricState.SUCCEEDED) return null
  const tierData = metric.data?.data?.[tier] as
    | Record<string, { x: number; y: number } | { x: number; y: number }[]>
    | undefined
  if (!tierData) return null
  const point = tierData[period]
  if (!point) return null
  if (Array.isArray(point)) {
    const first = point[0]
    return typeof first?.y === 'number' ? first.y : null
  }
  return typeof point?.y === 'number' ? point.y : null
}

type HeatStressOverviewProps = {
  propertyId: number
  emissionTier: string
}

function HeatStressOverview({ propertyId, emissionTier }: HeatStressOverviewProps) {
  const {
    data: heatScores,
    isLoading: heatScoresLoading,
    error: heatScoresError,
  } = usePropertyClimateData('hs:heat_scores', { propertyId })
  const {
    data: hotDays,
    isLoading: hotDaysLoading,
    error: hotDaysError,
  } = usePropertyClimateData('hs:hot_days', { propertyId })

  const overviewRows = useMemo(
    () =>
      PERIODS.map(period => ({
        period,
        label: canonicalPeriodLabels[period],
        heatScore: getCanonicalValue(heatScores, emissionTier, period),
        hotDays: getCanonicalValue(hotDays, emissionTier, period),
      })),
    [emissionTier, heatScores, hotDays],
  )

  const overviewHasData = overviewRows.some(row => row.heatScore != null || row.hotDays != null)
  const overviewLoading = ((heatScoresLoading && !heatScores) || (hotDaysLoading && !hotDays)) && !overviewHasData
  const overviewErrored = !overviewHasData && (heatScoresError != null || hotDaysError != null)

  return (
    <Card sx={{ p: 3 }}>
      <Box mb={2}>
        <Typography variant="h6">Overview</Typography>
      </Box>

      {overviewLoading && (
        <Box display="flex" alignItems="center" justifyContent="center" minHeight={140}>
          <CircularProgress size={24} />
        </Box>
      )}

      {!overviewLoading && overviewHasData && (
        <Grid container spacing={3}>
          {overviewRows.map(row => (
            <Grid item xs={12} md={4} key={row.period}>
              <Typography variant="subtitle2">{row.label}</Typography>
              <Box mt={1}>
                <Typography variant="overline" color="text.secondary">
                  Risk Rating
                </Typography>
                {/* <Typography variant="h5">{formatNumber(row.heatScore, 1)}</Typography> */}
                <Box>
                  <HeatStressScoreBadge score={row.heatScore ?? null} size="compact" />
                </Box>
              </Box>
              <Box mt={1}>
                <Typography variant="overline" color="text.secondary">
                  Local hot days
                </Typography>
                <Typography variant="h5">{formatNumber(row.hotDays, 0)} days</Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      )}

      {!overviewLoading && !overviewHasData && (
        <Alert severity={overviewErrored ? 'error' : 'info'}>
          {overviewErrored ? 'Unable to load heat stress overview data.' : 'No overview data available.'}
        </Alert>
      )}
    </Card>
  )
}

export default HeatStressOverview
